import { useState } from 'react'
import { Sparkles, Mail, Lock, User, ArrowRight, ArrowLeft, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { api } from '@/services/api-client'
import { useAuthStore } from '@/stores/auth'
import { useT } from '@/i18n'

interface AuthPageProps {
  mode: 'login' | 'register'
  onNavigate: (view: 'landing' | 'login' | 'register') => void
}

export function AuthPage({ mode, onNavigate }: AuthPageProps) {
  const setAuth = useAuthStore(s => s.setAuth)
  const t = useT()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (mode === 'register') {
        if (!displayName.trim()) {
          setError(t.authDisplayNameRequired)
          setLoading(false)
          return
        }
        const result = await api.register({
          email: email.trim(),
          password,
          display_name: displayName.trim(),
        })
        setAuth(result.user, result.token)
      } else {
        const result = await api.login({
          email: email.trim(),
          password,
        })
        setAuth(result.user, result.token)
      }
    } catch (err: any) {
      setError(err.message || t.authSomethingWrong)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-left">
          <div className="auth-left-content">
            <div className="auth-brand">
              <Sparkles size={32} />
              <h1>Synapsios</h1>
            </div>
            <p className="auth-tagline">
              {t.authTagline}
            </p>
            <div className="auth-features-list">
              <div className="auth-feature-item">
                <div className="auth-feature-dot" />
                <span>{t.authFeat1}</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-dot" />
                <span>{t.authFeat2}</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-dot" />
                <span>{t.authFeat3}</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-dot" />
                <span>{t.authFeat4}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-right">
          <button className="auth-back" onClick={() => onNavigate('landing')}>
            <ArrowLeft size={16} />
            {t.back}
          </button>

          <form className="auth-form" onSubmit={handleSubmit}>
            <h2>{mode === 'login' ? t.authSignIn : t.authCreateAccount}</h2>
            <p className="auth-form-subtitle">
              {mode === 'login'
                ? t.authWelcomeBack
                : t.authCreateNew}
            </p>

            {error && (
              <div className="auth-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {mode === 'register' && (
              <div className="auth-field">
                <label htmlFor="displayName">
                  <User size={16} />
                  <span>{t.authDisplayName}</span>
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder={t.authNamePlaceholder}
                  autoComplete="name"
                  required
                />
              </div>
            )}

            <div className="auth-field">
              <label htmlFor="email">
                <Mail size={16} />
                <span>{t.authEmail}</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t.authEmailPlaceholder}
                autoComplete="email"
                autoFocus
                required
              />
            </div>

            <div className="auth-field">
              <label htmlFor="password">
                <Lock size={16} />
                <span>{t.authPassword}</span>
              </label>
              <div className="auth-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? t.authMinChars : t.authYourPassword}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
              ) : (
                <>
                  {mode === 'login' ? t.authSignIn : t.authCreateAccount}
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <div className="auth-switch">
              {mode === 'login' ? (
                <p>
                  {t.authNoAccount}{' '}
                  <button type="button" onClick={() => onNavigate('register')}>
                    {t.authSignUp}
                  </button>
                </p>
              ) : (
                <p>
                  {t.authHasAccount}{' '}
                  <button type="button" onClick={() => onNavigate('login')}>
                    {t.authSignIn}
                  </button>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
